import './style.css';

interface AppState {
  usdCop: number;
  liveRateSource: 'en vivo' | 'referencia' | 'referencia base';
  lastChangedShare: 'model' | 'agency';
}

interface CalcInput {
  callDay: number;
  daysWeek: number;
  giftedDay: number;
  modelCount: number;
  modelShare: number;
  agencyShare: number;
  usdCop: number;
  investmentCop: number;
}

interface PeriodResult {
  grossUsd: number;
  grossCop: number;
  modelUsd: number;
  modelCop: number;
  agencyPerModelUsd: number;
  agencyPerModelCop: number;
  agencyTotalUsd: number;
  agencyTotalCop: number;
}

interface CalcResult {
  rows: Record<string, PeriodResult>;
  roiDays: number;
}

const DEFAULT_USD_COP = 3400;
const RATE_CALL = 0.26;
const RATE_GIFTED = 0.10;

const state: AppState = {
  usdCop: DEFAULT_USD_COP,
  liveRateSource: 'referencia base',
  lastChangedShare: 'model'
};

const els = {
  callMinutesDay: document.getElementById('callMinutesDay') as HTMLInputElement,
  daysWeek: document.getElementById('daysWeek') as HTMLInputElement,
  callMinutesWeek: document.getElementById('callMinutesWeek') as HTMLInputElement,
  giftedMinutesDay: document.getElementById('giftedMinutesDay') as HTMLInputElement,
  giftedMinutesWeek: document.getElementById('giftedMinutesWeek') as HTMLInputElement,
  modelCount: document.getElementById('modelCount') as HTMLInputElement,
  modelShare: document.getElementById('modelShare') as HTMLInputElement,
  agencyShare: document.getElementById('agencyShare') as HTMLInputElement,
  investmentCop: document.getElementById('investmentCop') as HTMLInputElement,
  investmentUsdDetail: document.getElementById('investmentUsdDetail') as HTMLElement,

  // Sidebar exchange rate display
  liveRateDisplay: document.getElementById('liveRateDisplay') as HTMLElement,
  liveRateStatus: document.getElementById('liveRateStatus') as HTMLElement,

  // Hero displays
  heroInvestmentCop: document.getElementById('heroInvestmentCop') as HTMLElement,
  heroInvestmentUsd: document.getElementById('heroInvestmentUsd') as HTMLElement,
  heroMonthlyAgencyCop: document.getElementById('heroMonthlyAgencyCop') as HTMLElement,
  heroMonthlyAgencyUsd: document.getElementById('heroMonthlyAgencyUsd') as HTMLElement,
  rateBadge: document.getElementById('rateBadge') as HTMLElement,
  shareBadge: document.getElementById('shareBadge') as HTMLElement,

  // Summary configuration strip
  summaryShare: document.getElementById('summaryShare') as HTMLElement,
  summaryModelCount: document.getElementById('summaryModelCount') as HTMLElement,
  summaryCallMins: document.getElementById('summaryCallMins') as HTMLElement,
  summaryGiftMins: document.getElementById('summaryGiftMins') as HTMLElement,
  summaryDaysWeek: document.getElementById('summaryDaysWeek') as HTMLElement,

  // Summary agency per-model earnings
  agencyPerModelDay: document.getElementById('agencyPerModelDay') as HTMLElement,
  agencyPerModelWeek: document.getElementById('agencyPerModelWeek') as HTMLElement,
  agencyPerModelMonth: document.getElementById('agencyPerModelMonth') as HTMLElement,

  // Summary agency total earnings (consolidated)
  agencyTotalDay: document.getElementById('agencyTotalDay') as HTMLElement,
  agencyTotalWeek: document.getElementById('agencyTotalWeek') as HTMLElement,
  agencyTotalMonth: document.getElementById('agencyTotalMonth') as HTMLElement,

  // Summary bottom row
  summaryInvestment: document.getElementById('summaryInvestment') as HTMLElement,
  summaryRoiDays: document.getElementById('summaryRoiDays') as HTMLElement,

  // Details
  detailCallDay: document.getElementById('detailCallDay') as HTMLElement,
  detailCallWeek: document.getElementById('detailCallWeek') as HTMLElement,
  detailGiftDay: document.getElementById('detailGiftDay') as HTMLElement,
  detailGiftWeek: document.getElementById('detailGiftWeek') as HTMLElement,
  detailModelShare: document.getElementById('detailModelShare') as HTMLElement,
  detailAgencyShare: document.getElementById('detailAgencyShare') as HTMLElement,
  detailImplicitRate: document.getElementById('detailImplicitRate') as HTMLElement,
  detailInvestment: document.getElementById('detailInvestment') as HTMLElement,

  // Single ROI Panel
  roiDays: document.getElementById('roiDays') as HTMLElement,
  roiDailyAgencyCop: document.getElementById('roiDailyAgencyCop') as HTMLElement,
  roiDailyAgencyUsd: document.getElementById('roiDailyAgencyUsd') as HTMLElement,

  // Tables
  earningsRows: document.getElementById('earningsRows') as HTMLElement,
  goalRows: document.getElementById('goalRows') as HTMLElement,
  resetBtn: document.getElementById('resetBtn') as HTMLButtonElement,
  toast: document.getElementById('toast') as HTMLElement
};

const SUGGESTED_GOALS = [
  { name: 'Inicio', callDay: 150, giftDay: 0 },
  { name: 'Base', callDay: 200, giftDay: 0 },
  { name: 'Bueno', callDay: 250, giftDay: 50 },
  { name: 'Fuerte', callDay: 300, giftDay: 100 },
  { name: 'Top', callDay: 400, giftDay: 200 }
];

const PERIOD_CONFIG = [
  { key: 'day', label: 'Día' },
  { key: 'week', label: 'Semana' },
  { key: 'biweek', label: 'Quincena' },
  { key: 'month', label: 'Mes' }
];

// Helper functions for parsing and formatting
function num(el: HTMLInputElement, fallback = 0): number {
  const value = Number(el.value);
  return Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function moneyUsd(value: number): string {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
  return `${formatted} USD`;
}

function moneyCop(value: number): string {
  const formatted = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
  const clean = formatted.trim();
  return `${clean} COP`;
}

function numberFormat(value: number, decimals = 1): string {
  return new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

function integerFormat(value: number): string {
  return new Intl.NumberFormat('es-CO', {
    maximumFractionDigits: 0
  }).format(value);
}

function showToast(text: string): void {
  els.toast.textContent = text;
  els.toast.classList.add('show');
  window.setTimeout(() => els.toast.classList.remove('show'), 2100);
}

function saveFormState(): void {
  const stateToSave = {
    callMinutesDay: els.callMinutesDay.value,
    daysWeek: els.daysWeek.value,
    giftedMinutesDay: els.giftedMinutesDay.value,
    modelCount: els.modelCount.value,
    modelShare: els.modelShare.value,
    agencyShare: els.agencyShare.value,
    investmentCop: els.investmentCop.value
  };
  localStorage.setItem('agency_simulator_form_state', JSON.stringify(stateToSave));
}

function loadFormState(): boolean {
  const saved = localStorage.getItem('agency_simulator_form_state');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (parsed) {
        els.callMinutesDay.value = parsed.callMinutesDay || '250';
        els.daysWeek.value = parsed.daysWeek || '5';
        els.giftedMinutesDay.value = parsed.giftedMinutesDay || '50';
        els.modelCount.value = parsed.modelCount || '4';
        els.modelShare.value = parsed.modelShare || '50';
        els.agencyShare.value = parsed.agencyShare || '50';
        els.investmentCop.value = parsed.investmentCop || '9000000';
        return true;
      }
    } catch (e) {
      console.error('Error loading saved form state:', e);
    }
  }
  return false;
}

function resetForm(): void {
  localStorage.removeItem('agency_simulator_form_state');
  els.callMinutesDay.value = '250';
  els.daysWeek.value = '5';
  els.giftedMinutesDay.value = '50';
  els.modelCount.value = '4';
  els.modelShare.value = '50';
  els.agencyShare.value = '50';
  els.investmentCop.value = '9000000';
  render();
  showToast('Valores restablecidos');
}

// Retrieve values from form inputs
function getInputs(): CalcInput {
  const callDay = Math.max(0, num(els.callMinutesDay));
  const daysWeek = clamp(num(els.daysWeek, 5), 1, 7);
  const giftedDay = Math.max(0, num(els.giftedMinutesDay));
  const modelCount = Math.max(1, Math.floor(num(els.modelCount, 4)));
  const modelShare = clamp(num(els.modelShare, 50), 0, 100);
  const agencyShare = clamp(num(els.agencyShare, 50), 0, 100);
  const investmentCop = Math.max(0, num(els.investmentCop, 9000000));

  return {
    callDay,
    daysWeek,
    giftedDay,
    modelCount,
    modelShare,
    agencyShare,
    usdCop: state.usdCop,
    investmentCop
  };
}

// Core Math Calculations
function calculate(v: CalcInput): CalcResult {
  const modelRatio = v.modelShare / 100;
  const agencyRatio = v.agencyShare / 100;

  const grossDay = v.callDay * RATE_CALL + v.giftedDay * RATE_GIFTED;
  
  const callWeek = v.callDay * v.daysWeek;
  const giftedWeek = v.giftedDay * v.daysWeek;
  const grossWeek = callWeek * RATE_CALL + giftedWeek * RATE_GIFTED;

  const byPeriod: Record<string, PeriodResult> = {};

  const periodMultipliers: Record<string, number> = {
    day: 1, // calculated using grossDay directly
    week: 1,
    biweek: 2,
    month: 4
  };

  Object.entries(periodMultipliers).forEach(([key, multiplier]) => {
    const gross = key === 'day' ? grossDay : grossWeek * multiplier;

    byPeriod[key] = {
      grossUsd: gross,
      grossCop: gross * v.usdCop,
      modelUsd: gross * modelRatio,
      modelCop: gross * modelRatio * v.usdCop,
      agencyPerModelUsd: gross * agencyRatio,
      agencyPerModelCop: gross * agencyRatio * v.usdCop,
      agencyTotalUsd: gross * agencyRatio * v.modelCount,
      agencyTotalCop: gross * agencyRatio * v.modelCount * v.usdCop
    };
  });

  const dailyAgencyTotalCop = byPeriod.day.agencyTotalCop;
  const roiDays = dailyAgencyTotalCop > 0 ? v.investmentCop / dailyAgencyTotalCop : Infinity;

  return { rows: byPeriod, roiDays };
}

// Formatting ROI outputs
function formatRoiValue(value: number, suffix = ''): string {
  if (!Number.isFinite(value) || value === Infinity) return 'No aplica';
  const formatted = numberFormat(value, value >= 10 ? 1 : 2);
  return suffix ? `${formatted} ${suffix}` : formatted;
}

function syncShares(changed: 'model' | 'agency'): void {
  if (changed === 'model') {
    const model = clamp(num(els.modelShare, 50), 0, 100);
    els.modelShare.value = String(model);
    els.agencyShare.value = String(100 - model);
  } else if (changed === 'agency') {
    const agency = clamp(num(els.agencyShare, 50), 0, 100);
    els.agencyShare.value = String(agency);
    els.modelShare.value = String(100 - agency);
  }
}

// Master Render loop
function render(): void {
  const v = getInputs();
  const res = calculate(v);
  const rows = res.rows;

  els.callMinutesWeek.value = String(Math.round(v.callDay * v.daysWeek));
  els.giftedMinutesWeek.value = String(Math.round(v.giftedDay * v.daysWeek));

  const investmentUsd = v.usdCop > 0 ? v.investmentCop / v.usdCop : 0;

  // Update Sidebar detail text and status card
  els.investmentUsdDetail.textContent = `≈ ${moneyUsd(investmentUsd)}`;
  els.liveRateDisplay.textContent = `${integerFormat(v.usdCop)} COP`;
  
  const sourceLabel = state.liveRateSource === 'en vivo' ? 'en vivo' : 'referencia';
  els.liveRateStatus.textContent = `Tasa ${sourceLabel} · Actualizado automáticamente`;

  // Update Hero elements
  els.heroInvestmentCop.textContent = moneyCop(v.investmentCop);
  els.heroInvestmentUsd.textContent = `≈ ${moneyUsd(investmentUsd)}`;
  els.heroMonthlyAgencyCop.textContent = moneyCop(rows.month.agencyTotalCop);
  els.heroMonthlyAgencyUsd.textContent = `≈ ${moneyUsd(rows.month.agencyTotalUsd)}`;
  els.rateBadge.textContent = `${integerFormat(v.usdCop)} COP/USD (${sourceLabel})`;
  els.shareBadge.textContent = `${v.modelShare}% Modelo / ${v.agencyShare}% Agencia`;

  // Update Summary config strip
  els.summaryShare.textContent = `${v.modelShare}/${v.agencyShare}`;
  els.summaryModelCount.textContent = integerFormat(v.modelCount);
  els.summaryCallMins.textContent = `${integerFormat(v.callDay)} min`;
  els.summaryGiftMins.textContent = `${integerFormat(v.giftedDay)} min`;
  els.summaryDaysWeek.textContent = `${integerFormat(v.daysWeek)} días`;

  const summaryMoneyHtml = (usd: number, cop: number) => `
    <span class="summary-cop-val">${moneyCop(cop)}</span>
    <span class="summary-usd-val">${moneyUsd(usd)}</span>
  `;

  // Update Summary agency per-model earnings
  els.agencyPerModelDay.innerHTML = summaryMoneyHtml(rows.day.agencyPerModelUsd, rows.day.agencyPerModelCop);
  els.agencyPerModelWeek.innerHTML = summaryMoneyHtml(rows.week.agencyPerModelUsd, rows.week.agencyPerModelCop);
  els.agencyPerModelMonth.innerHTML = summaryMoneyHtml(rows.month.agencyPerModelUsd, rows.month.agencyPerModelCop);

  // Update Summary agency total earnings (consolidated)
  els.agencyTotalDay.innerHTML = summaryMoneyHtml(rows.day.agencyTotalUsd, rows.day.agencyTotalCop);
  els.agencyTotalWeek.innerHTML = summaryMoneyHtml(rows.week.agencyTotalUsd, rows.week.agencyTotalCop);
  els.agencyTotalMonth.innerHTML = summaryMoneyHtml(rows.month.agencyTotalUsd, rows.month.agencyTotalCop);

  // Update Summary ROI & investment bottom strip
  els.summaryInvestment.innerHTML = summaryMoneyHtml(investmentUsd, v.investmentCop);
  els.summaryRoiDays.textContent = formatRoiValue(res.roiDays, 'días');

  // Render main detailed earnings table rows
  const moneyCellHtml = (usd: number, cop: number) => `
    <div class="table-money-cell">
      <strong>${moneyCop(cop)}</strong>
      <small>${moneyUsd(usd)}</small>
    </div>
  `;

  els.earningsRows.innerHTML = PERIOD_CONFIG.map(p => {
    const row = rows[p.key];
    return `
      <tr>
        <td><strong>${p.label}</strong></td>
        <td>${moneyCellHtml(row.grossUsd, row.grossCop)}</td>
        <td>${moneyCellHtml(row.modelUsd, row.modelCop)}</td>
        <td>${moneyCellHtml(row.agencyPerModelUsd, row.agencyPerModelCop)}</td>
        <td>${moneyCellHtml(row.agencyTotalUsd, row.agencyTotalCop)}</td>
      </tr>
    `;
  }).join('');

  // Render bottom detail activity list
  els.detailCallDay.textContent = integerFormat(v.callDay);
  els.detailCallWeek.textContent = integerFormat(v.callDay * v.daysWeek);
  els.detailGiftDay.textContent = integerFormat(v.giftedDay);
  els.detailGiftWeek.textContent = integerFormat(v.giftedDay * v.daysWeek);
  els.detailModelShare.textContent = `${v.modelShare}%`;
  els.detailAgencyShare.textContent = `${v.agencyShare}%`;
  els.detailInvestment.textContent = `${moneyCop(v.investmentCop)} / ${moneyUsd(investmentUsd)}`;
  
  const implicitRate = investmentUsd > 0 ? v.investmentCop / investmentUsd : 0;
  els.detailImplicitRate.textContent = implicitRate > 0 ? `${numberFormat(implicitRate, 2)} COP/USD` : 'No aplica';

  // Render single ROI panel displays
  els.roiDays.textContent = formatRoiValue(res.roiDays, 'días');
  els.roiDailyAgencyCop.textContent = moneyCop(rows.day.agencyTotalCop);
  els.roiDailyAgencyUsd.textContent = `≈ ${moneyUsd(rows.day.agencyTotalUsd)} / día`;

  // Render suggested production goals table rows
  els.goalRows.innerHTML = SUGGESTED_GOALS.map(goal => {
    const weeklyCallGoal = goal.callDay * v.daysWeek;
    const weeklyGiftedGoal = goal.giftDay * v.daysWeek;

    const grossWeekGoal = weeklyCallGoal * RATE_CALL + weeklyGiftedGoal * RATE_GIFTED;
    const modelWeekUsd = grossWeekGoal * (v.modelShare / 100);
    const modelMonthUsd = modelWeekUsd * 4;
    const agencyMonthUsd = grossWeekGoal * 4 * (v.agencyShare / 100) * v.modelCount;

    return `
      <tr>
        <td><strong>${goal.name}</strong></td>
        <td>${integerFormat(goal.callDay)}</td>
        <td>${integerFormat(goal.giftDay)}</td>
        <td>${moneyCellHtml(modelWeekUsd, modelWeekUsd * v.usdCop)}</td>
        <td>${moneyCellHtml(modelMonthUsd, modelMonthUsd * v.usdCop)}</td>
        <td>${moneyCellHtml(agencyMonthUsd, agencyMonthUsd * v.usdCop)}</td>
      </tr>
    `;
  }).join('');
}

// Live Exchange Rate integration
async function fetchLiveRate(): Promise<void> {
  try {
    const response = await fetch('https://open.er-api.com/v6/latest/USD', { cache: 'no-store' });
    if (!response.ok) throw new Error('API request failed');
    const data = await response.json() as { rates?: Record<string, number> };
    const rateCop = data?.rates?.COP;
    if (!rateCop || typeof rateCop !== 'number' || rateCop <= 0) {
      throw new Error('Invalid conversion rate received');
    }
    state.usdCop = Math.round(rateCop);
    state.liveRateSource = 'en vivo';
  } catch (err) {
    state.usdCop = DEFAULT_USD_COP;
    state.liveRateSource = 'referencia base';
  }
  render();
}

// Event Bindings
function bindEvents(): void {
  // Watch share updates to sync splits to 100%
  els.modelShare.addEventListener('input', () => {
    state.lastChangedShare = 'model';
    syncShares('model');
    render();
    saveFormState();
  });

  els.agencyShare.addEventListener('input', () => {
    state.lastChangedShare = 'agency';
    syncShares('agency');
    render();
    saveFormState();
  });

  // General inputs watch
  const inputsToWatch = [
    els.callMinutesDay,
    els.daysWeek,
    els.giftedMinutesDay,
    els.modelCount,
    els.investmentCop
  ];

  inputsToWatch.forEach(input => {
    input.addEventListener('input', () => {
      render();
      saveFormState();
    });
    input.addEventListener('change', () => {
      render();
      saveFormState();
    });
  });

  els.resetBtn.addEventListener('click', resetForm);
}

// Run Initialization
function init(): void {
  loadFormState();
  bindEvents();
  render();
  void fetchLiveRate();
}

init();
